import React from 'react';
import { Award, Users, Shield, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

export const About = () => {
  const navigate = useNavigate();
  
  const values = [
    {
      icon: Shield,
      title: 'Professionalism',
      description: 'Licensed, insured, and certified to deliver exceptional service'
    },
    {
      icon: Users,
      title: 'Trust',
      description: 'Background-checked staff you can rely on in your home'
    },
    {
      icon: Award,
      title: 'Quality',
      description: '20+ years combined experience ensuring top results'
    },
    {
      icon: Heart,
      title: 'Consistency',
      description: 'Reliable service that exceeds expectations every time'
    }
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                About <span className="text-[#0B7CB5]">Custom Maids RVA</span>
              </h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  As a <strong>Richmond native</strong> and <strong>woman-owned business</strong>, Custom Maids RVA was born from a simple truth: a clean space creates a clear mind. With a background in nursing, I've always been drawn to helping others. I understand firsthand the overwhelming weight of daily responsibilities and how a cluttered home can add to life's stresses.
                </p>
                <p>
                  I started this business to serve <strong>exhausted mothers</strong> balancing family and work, <strong>elderly individuals</strong> who need compassionate support, and <strong>busy professionals</strong> who deserve to come home to peace instead of chores. Cleaning isn't just about tidying up—it's about restoring your mental and emotional well-being.
                </p>
                <p>
                  Every surface we clean, every room we refresh, is our way of giving you back precious time and energy. We're <strong>SWaM-certified</strong> with <strong>20+ years of combined experience</strong>, and we treat every home with the same care we'd give our own.
                </p>
                <div className="bg-[#0B7CB5]/10 border-l-4 border-[#0B7CB5] p-6 rounded-r-lg mt-6">
                  <p className="text-xl font-semibold text-gray-900 mb-2">Our Mission</p>
                  <p className="text-gray-700">
                    To lighten your load and restore peace to your space, so you can focus on what truly matters—your family, your health, and your happiness.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_c5e5208d-6c87-43b5-ba57-a4994d46a238/artifacts/0z9w6plz_IMG_0140.jpg"
                alt="Custom Maids RVA Owner - Richmond, VA"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <p className="text-4xl font-bold text-[#0B7CB5]">2.5+</p>
                <p className="text-gray-700 font-medium">Years Serving<br />Richmond</p>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Our <span className="text-[#0B7CB5]">Core Values</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => {
                const IconComponent = value.icon;
                return (
                  <div
                    key={index}
                    className="text-center p-6 bg-gradient-to-b from-[#0B7CB5]/5 to-white rounded-xl hover:shadow-lg transition-all duration-300 hover:-translate-y-2 group"
                  >
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md group-hover:shadow-xl transition-shadow duration-300">
                      <IconComponent className="w-8 h-8 text-[#0B7CB5]" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h4>
                    <p className="text-gray-600">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ready for Spotless Home CTA */}
          <div className="text-center bg-gradient-to-r from-[#0B7CB5]/10 to-[#5CC4E8]/10 rounded-2xl p-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ready for Your <span className="text-[#0B7CB5]">Spotless Home?</span>
            </h3>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Let us take the burden of cleaning off your shoulders. Book your service today!
            </p>
            <Button
              onClick={() => navigate('/calculator')}
              size="lg"
              className="bg-[#0B7CB5] hover:bg-[#095a87] text-white px-10 py-6 text-lg"
            >
              Book Now
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};